/**
 * ScanService — walks a folder and registers the media it holds.
 *
 * Single job: turning a directory into rows. It does not generate thumbnails,
 * does not assign buckets, and does not decide which folder to scan.
 */

import type { DirectoryWalker } from '../adapters/directory-walker';
import type { FsDirectory } from '../adapters/fs-port';
import type { MediaRepository, NewMediaRow } from '../repositories';
import type { ScanProgress } from '../types';
import type { ChangeSink, Clock } from './ports';

export interface ScanResult {
  sourceId: number;
  scanned: number;
  added: number;
  skipped: number;
  cancelled: boolean;
}

export class ScanService {
  constructor(
    private readonly walker: DirectoryWalker,
    private readonly media: MediaRepository,
    private readonly sink: ChangeSink,
    private readonly clock: Clock,
  ) {}

  async scan(
    projectId: number,
    sourceId: number,
    directory: FsDirectory,
    onProgress?: (progress: ScanProgress) => void,
    signal?: { aborted: boolean },
  ): Promise<ScanResult> {
    const report = (phase: ScanProgress['phase'], scanned: number, added: number, skipped: number) =>
      onProgress?.({
        projectId,
        sourceName: directory.name,
        phase,
        scanned,
        added,
        skipped,
        thumbsDone: 0,
        thumbsTotal: 0,
      });

    report('walking', 0, 0, 0);

    const found = await this.walker.walk(directory, {
      signal,
      onProgress: (count, skipped) => report('walking', count, 0, skipped),
    });

    if (signal?.aborted) {
      report('cancelled', found.length, 0, 0);
      return { sourceId, scanned: found.length, added: 0, skipped: 0, cancelled: true };
    }

    report('inserting', found.length, 0, 0);

    const rows: NewMediaRow[] = found.map((file) => ({
      relPath: file.relPath,
      filename: file.filename,
      type: file.type,
      ext: file.ext,
      sizeBytes: file.sizeBytes,
      modifiedAt: file.modifiedAt,
    }));

    const added = this.media.insertMany(projectId, sourceId, rows, this.clock());
    if (added > 0) this.sink.touch();

    const skipped = found.length - added;
    report('done', found.length, added, skipped);

    return { sourceId, scanned: found.length, added, skipped, cancelled: false };
  }
}
